import React, { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { profilePhoto, otherProfilePhoto } from '../../actions/profileTrackAction';
import { areEqual, triggerTracking } from '../../utils/helpers';
import { HeartNotificationIcon, OfferNew } from '../icons/favoriteIcons';
import ProfilePhotoSliderCard from './ProfilePhotoSliderCard';
import { useProfile } from '../../contexts/ProfileContext';
import breakpoints from 'theme/material/breakpoints';

const fadeInOut = keyframes`
  0% {
    opacity: 1;
    margin-top: 0px;
  }
  80% {
    margin-top: 0px;
  }
  90% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.7;
    margin-top: -27px;
  }
`;

const ProfileCardBanner = styled('div')<{ customBackground: string }>`
  position: relative;
  padding: 6px 0;
  width: 100%;
  z-index: 5;
  display: flex;
  justify-content: center;
  font-size: 14px;
  color: #fff;
  opacity: 0.9, svg {
    width: 14px;
    height: 14px;
    margin-top: 1px;
    margin-right: 4px;
  }
  ${breakpoints.down(639.05)} {
    font-size: 12px;
    svg {
      height: 12px;
    }
  }
  background: ${props => (props.customBackground ? props.customBackground : 'rgba(188,32,40,0.9)')};
  animation: ${fadeInOut} 3.5s ease-in-out forwards;
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const SliderWrapper = styled('div')(() => ({
  position: 'relative',
}));

const setPhotoAction = (e, profileStatus) => {
  switch (profileStatus) {
    case 'myProfile':
      profilePhoto();
      break;
    case 'otherProfile':
      otherProfilePhoto();
      break;
    default:
      return;
  }
};

interface ProfilePhotoSliderV2Props {
  photoList: Array<unknown>;
  onClick: (index: number, e: React.MouseEvent) => void;
  offer: any;
  isOtherProfile: boolean;
  profileDetailsComponent: any;
}

const ProfilePhotoSliderV2 = ({
  photoList,
  onClick,
  offer,
  isOtherProfile,
  profileDetailsComponent,
}: ProfilePhotoSliderV2Props): JSX.Element => {
  const { profileCardBannerRef } = useProfile();
  const myProfile = useRef(null);
  const otherProfile = useRef(null);
  const hasOfferStatus = useMemo(() => offer && offer.status, [offer]);

  useEffect(() => {
    triggerTracking('myProfile', myProfile, setPhotoAction);
    triggerTracking('otherProfile', otherProfile, setPhotoAction);
  }, []);

  const renderProfileCardBanner = () => {
    if (hasOfferStatus) {
      if (offer.status === 'accepted') {
        return (
          <ProfileCardBanner
            data-test-id="profile-banner-offer-accepted"
            customBackground="linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%)"
            ref={profileCardBannerRef}
          >
            <HeartNotificationIcon /> Offer Accepted!
          </ProfileCardBanner>
        );
      }

      if (offer.status === 'pending') {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        if (offer.to_account_hash_id === myProfile.hash_id) {
          return (
            <ProfileCardBanner
              ref={profileCardBannerRef}
              data-test-id={offer.suggest ? 'profile-banner-suggest-request' : 'profile-banner-offer-request'}
              customBackground="rgb(191, 146, 0)"
            >
              {offer.suggest ? 'Asked you to Offer!' : 'Sent You an Offer!'}
            </ProfileCardBanner>
          );
        }

        return (
          <ProfileCardBanner
            data-test-id="profile-banner-pending-offer"
            customBackground="rgb(191, 146, 0)"
            ref={profileCardBannerRef}
          >
            <OfferNew fill="rgba(255,255,255,1)" /> Pending Offer
          </ProfileCardBanner>
        );
      }
    }

    return '';
  };

  return (
    <>
      {renderProfileCardBanner()}
      <SliderWrapper>
        <ProfilePhotoSliderCard
          photoList={photoList}
          onClickPhoto={onClick}
          isOtherProfile={isOtherProfile}
          profileDetailsComponent={profileDetailsComponent}
        />
      </SliderWrapper>
    </>
  );
};

export default React.memo(ProfilePhotoSliderV2, areEqual);
